import type { routes } from '@semios/app-platform-banyan-route-definitions'
import type { TGroupedTableSection } from 'App/dashboards/_components/DashboardGroupedTable'
import { compareLabelsNullsLast } from 'App/dashboards/_utils/compareLabelsNullsLast'
import { getCropNameFromId } from 'utils/getCropNameFromId'

export type TZoneCrops = {
  cropIdsByZoneId: Record<number, number[]>
  multiCropPropertyIds: Set<number>
}

export const buildZoneCrops = (zones: routes.IrrigationZoneCrops.Response['zones']): TZoneCrops => {
  const cropIdsByZoneId: Record<number, number[]> = {}
  const cropIdsByPropertyId: Record<number, Set<number>> = {}

  zones.forEach(({ zoneId, propertyId, cropIds }) => {
    cropIdsByZoneId[zoneId] = cropIds

    cropIdsByPropertyId[propertyId] ??= new Set()

    cropIds.forEach((cropId) => cropIdsByPropertyId[propertyId].add(cropId))
  })

  const multiCropPropertyIds = new Set(
    Object.entries(cropIdsByPropertyId)
      .filter(([, cropIds]) => cropIds.size > 1)
      .map(([propertyId]) => Number(propertyId)),
  )

  return { cropIdsByZoneId, multiCropPropertyIds }
}

export const getZonesCropName = (
  zoneIds: number[],
  cropIdsByZoneId: Record<number, number[]>,
): string | null => {
  const cropIds = [...new Set(zoneIds.flatMap((zoneId) => cropIdsByZoneId[zoneId] ?? []))]

  if (!cropIds.length) return null

  return cropIds
    .map((cropId) => getCropNameFromId(cropId))
    .sort((a, b) => a.localeCompare(b))
    .join(', ')
}

export const withCropNames = <Row>(
  sections: TGroupedTableSection<Row>[],
  getZoneIds: (row: Row) => number[],
  zoneCrops: TZoneCrops,
  groupByCrop: boolean,
): TGroupedTableSection<Row & { cropName: string | null }>[] =>
  sections.map((section) => {
    const rows = section.rows.map((row) => ({
      ...row,
      cropName: getZonesCropName(getZoneIds(row), zoneCrops.cropIdsByZoneId),
    }))

    return {
      ...section,
      rows: groupByCrop ? rows.sort((a, b) => compareLabelsNullsLast(a.cropName, b.cropName)) : rows,
    }
  })
